import { useState } from 'react';
import {
  ShieldCheck,
  Search,
  CheckCircle2,
  XCircle,
  FileText,
  Upload,
  Lock,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { httpClient } from '@/api';

interface ValidationResult {
  autentico: boolean;
  assinadoDigitalmenteIcp: boolean;
  padraoCertificacao: string;
  prescriptionUuid: string;
  fileHashSha256: string;
  dataEmissao: string;
  possuiCarimboTempo: boolean;
  tiposDocumentos: string[];
  signaturePdfUrl?: string;
  prescritor: {
    nome: string;
    crm: string | null;
    especialidade: string;
  };
}

export function PublicValidatePrescriptionPage() {
  const [term, setTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async (targetTerm = term) => {
    const clean = targetTerm.trim();
    if (!clean) return;

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await httpClient.get<ValidationResult>(
        `/validar-receita/consulta?termo=${encodeURIComponent(clean)}`,
      );
      setResult(res);
    } catch (err: any) {
      setError(
        err?.message ||
          'Documento não localizado no barramento de validação da Medikei. Verifique o código e tente novamente.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

      setTerm(hashHex);
      await handleValidate(hashHex);
    } catch {
      setError('Erro ao processar o arquivo PDF para cálculo do hash SHA-256.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col justify-between selection:bg-primary/20 selection:text-primary">
      {/* Header com Logo Oficial */}
      <header className="border-b border-border bg-card/80 backdrop-blur-md px-4 sm:px-8 py-4 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Medikei" className="h-8 sm:h-9 w-auto dark:brightness-110" />
            <div className="h-5 w-px bg-border hidden sm:block" />
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider hidden sm:block">
              Validador Oficial de Documentos Clínicos
            </span>
          </Link>

          <Link to="/login" className="text-xs font-semibold text-primary hover:underline">
            Acessar Plataforma &rarr;
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8 sm:py-12 space-y-6">
        <div className="text-center space-y-3">
          <div className="inline-flex p-3 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shadow-xs">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
            Validação de Autenticidade Documental ICP-Brasil
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Consulte a integridade e conformidade de receitas, atestados e exames emitidos pela Medikei nos termos da
            Medida Provisória 2.200-2/2001 e Resolução CFM 2.314/2022.
          </p>
        </div>

        {/* Formulário de Busca e Upload */}
        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
              Identificador (UUID) ou Hash SHA-256 do Documento
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Ex: c181503-xxxx ou cole o hash SHA-256..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
                  className="pl-10 h-11 rounded-xl font-mono text-xs"
                />
              </div>
              <Button
                onClick={() => handleValidate()}
                disabled={isLoading || !term.trim()}
                className="h-11 px-6 rounded-xl font-bold gap-2 cursor-pointer"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span>Verificar</span>
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] text-muted-foreground font-semibold uppercase">ou envie o arquivo PDF</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div>
            <label className="flex items-center justify-center gap-2 p-3.5 border border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-muted/40 rounded-xl cursor-pointer transition-colors text-xs text-muted-foreground hover:text-foreground">
              <Upload className="h-4 w-4 text-primary" />
              <span>Clique aqui para carregar o arquivo PDF e calcular o hash criptográfico</span>
              <input type="file" accept="application/pdf" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        </div>

        {/* Estado de Erro */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-5 flex items-start gap-3.5 text-xs text-destructive animate-in fade-in">
            <XCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm">Não foi possível confirmar a autenticidade</p>
              <p className="mt-1 leading-relaxed text-destructive/90">{error}</p>
            </div>
          </div>
        )}

        {/* Resultado Positivo de Validação */}
        {result && (
          <div className="bg-card border border-emerald-500/30 rounded-2xl p-6 shadow-md space-y-5 animate-in fade-in">
            {/* Selo Principal */}
            <div className="flex items-center justify-between gap-3 pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base text-foreground">
                    Documento Autêntico e Íntegro
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                    Certificação Digital ICP-Brasil Válida
                  </p>
                </div>
              </div>

              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-mono text-xs">
                CONFORME
              </Badge>
            </div>

            {/* Metadados do Prescritor e Documento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-muted/30 border border-border rounded-xl space-y-1">
                <span className="text-[10px] uppercase font-bold text-muted-foreground block">
                  Profissional Prescritor
                </span>
                <p className="font-bold text-foreground text-sm">{result.prescritor.nome}</p>
                <p className="text-muted-foreground">
                  {result.prescritor.crm ? `CRM: ${result.prescritor.crm}` : 'Registro Ativo'} &bull;{' '}
                  {result.prescritor.especialidade}
                </p>
              </div>

              <div className="p-3 bg-muted/30 border border-border rounded-xl space-y-1">
                <span className="text-[10px] uppercase font-bold text-muted-foreground block">
                  Data e Carimbo de Tempo
                </span>
                <p className="font-bold text-foreground text-sm">
                  {new Date(result.dataEmissao).toLocaleString('pt-BR')}
                </p>
                <p className="text-muted-foreground">{result.padraoCertificacao}</p>
              </div>
            </div>

            {/* Hash Criptográfico SHA-256 */}
            <div className="p-3.5 bg-muted/40 border border-border rounded-xl space-y-1">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block flex items-center gap-1">
                <Lock className="h-3 w-3 text-primary" /> Hash Criptográfico SHA-256 (Integridade do Arquivo)
              </span>
              <p className="font-mono text-[11px] text-primary break-all select-all">{result.fileHashSha256}</p>
            </div>

            {/* Ações */}
            {result.signaturePdfUrl && (
              <div className="pt-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => window.open(result.signaturePdfUrl, '_blank')}
                  className="rounded-xl text-xs font-bold gap-1.5 h-10 px-4 cursor-pointer"
                >
                  <FileText className="h-4 w-4" />
                  <span>Visualizar Certificado ICP-Brasil</span>
                  <ExternalLink className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-4 text-center text-xs text-muted-foreground">
        <p>Medikei Saúde &copy; {new Date().getFullYear()} &bull; Plataforma Oficial de Telemedicina e Saúde Digital</p>
      </footer>
    </div>
  );
}
